import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import { userLocationAtom, isLocationFetchingAtom, LOCATION_CACHE_MS, UserLocation } from 'store/locationStore';
import { isNativeApp, getNativeCurrentLocation } from 'common/webview';

// 진행 중인 위치 요청 Promise (모듈 레벨 — 중복 요청 방지)
let pendingRequest: Promise<UserLocation | null> | null = null;

/**
 * 사용자 현재 위치 관리 Hook
 */
export const useUserLocation = () => {
    const [location, setLocation] = useAtom(userLocationAtom);
    const [isFetching, setIsFetching] = useAtom(isLocationFetchingAtom);

    /** 실제 위치 요청 — 네이티브 → 브라우저 순서로 시도 */
    // 좌표가 유효한 범위인지 검증 (이동 중 부정확한 좌표 방어)
    const isValidCoord = (lat: any, lng: any): boolean => {
        if (typeof lat !== 'number' || typeof lng !== 'number') return false;
        if (isNaN(lat) || isNaN(lng)) return false;
        if (lat === 0 && lng === 0) return false; // 초기값/에러 기본값 거부
        if (lat < -90 || lat > 90) return false;
        if (lng < -180 || lng > 180) return false;
        return true;
    };

    const doFetch = async (): Promise<UserLocation | null> => {
        // 1. 네이티브 앱 (타임아웃 3초)
        if (isNativeApp()) {
            try {
                const loc = await getNativeCurrentLocation();
                if (loc && isValidCoord(loc.lat, loc.lng)) {
                    console.log('[Location] 네이티브 위치 성공:', loc.lat, loc.lng);
                    return { lat: loc.lat, lng: loc.lng, timestamp: Date.now() };
                }
                console.log('[Location] 네이티브 위치 실패/유효 아님, 브라우저 fallback:', loc);
            } catch {
                console.log('[Location] 네이티브 위치 에러, 브라우저 fallback');
            }
        }

        // 2. 브라우저 Geolocation
        if (navigator.geolocation) {
            try {
                return await new Promise<UserLocation | null>((resolve) => {
                    const timer = setTimeout(() => {
                        console.log('[Location] 브라우저 위치 타임아웃');
                        resolve(null);
                    }, 8000);
                    navigator.geolocation.getCurrentPosition(
                        (pos) => {
                            clearTimeout(timer);
                            const { latitude, longitude } = pos.coords;
                            if (!isValidCoord(latitude, longitude)) {
                                console.log('[Location] 브라우저 위치 유효 아님:', latitude, longitude);
                                resolve(null);
                                return;
                            }
                            console.log('[Location] 브라우저 위치 성공:', latitude, longitude);
                            resolve({ lat: latitude, lng: longitude, timestamp: Date.now() });
                        },
                        (err) => {
                            clearTimeout(timer);
                            console.log('[Location] 브라우저 위치 실패:', err.code, err.message);
                            resolve(null);
                        },
                        { enableHighAccuracy: false, timeout: 7000, maximumAge: LOCATION_CACHE_MS }
                    );
                });
            } catch {
                console.log('[Location] 브라우저 Geolocation 에러');
            }
        }

        console.log('[Location] 모든 위치 소스 실패');
        return null;
    };

    /** 위치 요청 (캐시 유효하면 즉시 반환) */
    const fetchLocation = useCallback(async (force = false): Promise<UserLocation | null> => {
        // 캐시 유효하면 즉시 반환 (atom에서 직접 읽음)
        if (!force && location && Date.now() - location.timestamp < LOCATION_CACHE_MS) {
            console.log('[Location] 캐시 히트:', location.lat, location.lng);
            return location;
        }

        // 이미 요청 중이면 같은 Promise 재사용
        if (pendingRequest) {
            console.log('[Location] 기존 요청 대기 중');
            return pendingRequest;
        }

        console.log('[Location] 새 위치 요청 시작');
        setIsFetching(true);

        pendingRequest = doFetch().then(result => {
            if (result) {
                setLocation(result);
            }
            setIsFetching(false);
            pendingRequest = null;
            return result;
        }).catch(() => {
            setIsFetching(false);
            pendingRequest = null;
            return null;
        });

        return pendingRequest;
    }, [location, setLocation, setIsFetching]);

    // 네이티브에서 백그라운드 정밀 측위 결과 수신 → atom 자동 갱신
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail?.lat && detail?.lng) {
                console.log('[Location] 백그라운드 정밀 위치 업데이트:', detail.lat, detail.lng);
                setLocation({ lat: detail.lat, lng: detail.lng, timestamp: Date.now() });
            }
        };
        window.addEventListener('location-update', handler);
        return () => window.removeEventListener('location-update', handler);
    }, [setLocation]);

    return { location, isFetching, fetchLocation };
};
